// PRD 07 §3.1, §3.5 — list orchestrator: filter bar, table, and
// loading/empty/no-search-match/error states.
//
// Slice 2 is read-only. The "+ Add Management Guideline" button, row click,
// delete column, and pagination land in Slices 4–5 and Slice 3 respectively.
// Per-page selector renders the PRD-fixed "20 per page" value as a static
// dropdown for now.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Book, Search } from 'lucide-react';

import { useCan } from '@/features/roles/hooks/use-can';
import { Pagination } from '@/components/data/Pagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/constants/pagination';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { useGuidelinesQuery } from '../hooks/use-guidelines';
import { useManagementGuidelinesPageStore } from '../store/management-guidelines-page.store';
import type { ManagementGuideline } from '../types/management-guidelines.types';
import { filterGuidelines, sortGuidelines } from '../utils/filter-sort';
import { DeleteGuidelineConfirm } from './DeleteGuidelineConfirm';
import { GuidelineFormPanel } from './GuidelineFormPanel';
import { GuidelineTable } from './GuidelineTable';

// PRD §3.5 — real-time filtering with prototype-matching 300 ms debounce.
const SEARCH_DEBOUNCE_MS = 300;

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="animate-pulse"
          style={{
            height: 40,
            background: 'var(--muted)',
            borderRadius: 'var(--radius-sm)',
          }}
        />
      ))}
    </div>
  );
}

function TableError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation('management-guidelines');
  return (
    <div
      role="alert"
      style={{
        margin: 16,
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 'var(--radius-md)',
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
      }}
    >
      <span>{message}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('list.retry')}
      </button>
    </div>
  );
}

function EmptyState({ title }: { title: string }) {
  // Wrap the SVG in <i> so the prototype's `.table-empty i { display: inline-block }`
  // rule (globals.css) applies and the icon centers above the text.
  return (
    <div className="table-empty">
      <i>
        <Book size={48} color="var(--muted-foreground)" />
      </i>
      <p className="table-empty__title">{title}</p>
    </div>
  );
}

export function ManagementGuidelinesView() {
  const { t } = useTranslation('management-guidelines');
  const canWrite = useCan('METADATA', 'WRITE');

  const search = useManagementGuidelinesPageStore((s) => s.search);
  const setSearch = useManagementGuidelinesPageStore((s) => s.setSearch);
  const sortKey = useManagementGuidelinesPageStore((s) => s.sortKey);
  const sortDir = useManagementGuidelinesPageStore((s) => s.sortDir);
  const toggleSort = useManagementGuidelinesPageStore((s) => s.toggleSort);
  const page = useManagementGuidelinesPageStore((s) => s.page);
  const setPage = useManagementGuidelinesPageStore((s) => s.setPage);
  const openEdit = useManagementGuidelinesPageStore((s) => s.openEdit);

  // Delete target lives in local state so the confirm modal closes cleanly
  // without depending on page-store internals (the form panel will follow the
  // same pattern in Slice 5).
  const [deleteTarget, setDeleteTarget] = useState<ManagementGuideline | null>(null);

  const { data, isLoading, error, refetch } = useGuidelinesQuery();

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const visibleRows = useMemo(() => {
    if (!data) return [];
    return sortGuidelines(filterGuidelines(data, debouncedSearch), sortKey, sortDir);
  }, [data, debouncedSearch, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(visibleRows.length / DEFAULT_PAGE_SIZE));

  // Clamp `page` if filtering shrank the total below the current page (e.g.
  // user on page 3, then narrows the search). Effect (not derived value) so
  // the URL-of-truth — store state — stays consistent with what the
  // Pagination component reads.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages, setPage]);

  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * DEFAULT_PAGE_SIZE;
    return visibleRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [visibleRows, safePage]);

  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <>
      <div className="filter-bar">
        <div className="filter-bar__search">
          <input
            type="text"
            className="filter-bar__search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('list.searchPlaceholder')}
            aria-label={t('list.searchPlaceholder')}
          />
          <Search size={16} className="filter-bar__search-icon" />
        </div>
        {/* PRD §2.2 #2 — fixed "20 per page" select. Real per-page selection
            arrives if/when BE relaxes the cap. */}
        <select
          className="filter-bar__select"
          value={DEFAULT_PAGE_SIZE}
          aria-label={t('list.itemsPerPage', {
            count: DEFAULT_PAGE_SIZE,
          })}
          onChange={() => {
            /* no-op for Slice 2 */
          }}
        >
          <option value={DEFAULT_PAGE_SIZE}>
            {t('list.itemsPerPage', { count: DEFAULT_PAGE_SIZE })}
          </option>
        </select>
      </div>

      <div className="card">
        {isLoading ? (
          <TableSkeleton />
        ) : error ? (
          <TableError
            message={t('list.loadError')}
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            {/* Prototype parity (management-guidelines.html): the table header
                stays mounted on empty results; the empty-state notice renders
                as a sibling below the empty <tbody>. */}
            <GuidelineTable
              rows={pageRows}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={toggleSort}
              onRowOpen={(row) => openEdit(row.gid)}
              canWrite={canWrite}
              onDelete={setDeleteTarget}
            />
            {visibleRows.length === 0 && (
              <EmptyState
                title={hasSearch ? t('list.emptyNoMatch') : t('list.empty')}
              />
            )}
          </>
        )}
      </div>
      <Pagination page={safePage} totalPages={totalPages} onPageChange={setPage} />

      <DeleteGuidelineConfirm
        guideline={deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
      <GuidelineFormPanel />
    </>
  );
}
